/**
 * Le volume d'une routine, muscle par muscle.
 *
 * 🔴 **Dérivé de la RÉPLIQUE, pas demandé au serveur** — exactement comme
 * `exerciseFacets` à l'étape 4, et pour les deux mêmes raisons : la carte
 * fonctionne **sans réseau**, et elle ne peut pas se désaccorder de la liste
 * d'exercices qu'elle résume, puisqu'elle en sort. Une route dédiée aurait
 * offert les deux occasions de diverger.
 *
 * ⚠️ **`RoutineLineDTO` ne porte pas les muscles**, et il n'a pas à les porter :
 * la liste des routines est répliquée entière (§3.1), et y ajouter les muscles
 * de chaque ligne la ferait grossir pour une information que la réplique
 * d'exercices contient déjà. On croise les deux à la lecture.
 *
 * 🔴 **Un muscle SECONDAIRE compte pour moitié — et la règle vit désormais
 * dans `shared/muscles.ts`** (V2 étape 9), parce que le tableau de bord compte
 * lui aussi des séries par muscle, à partir des `actual_set`. Le raisonnement
 * est inchangé et reste ici pour mémoire : ni un ni zéro : une série de
 * développé couché ne travaille pas les triceps autant que les pectoraux, mais
 * elle les travaille. Les compter pleins ferait dire à la carte que tout est
 * sollicité pareil ; les ignorer laisserait des bras blancs sur une séance qui
 * les a bien employés. Le demi est un choix d'affichage, assumé — la carte
 * **situe**, elle ne mesure pas.
 *
 * ⚠️ **Les séries sont comptées tours de circuit compris**, comme
 * `routineWeight()` : un circuit de 4 tours sur une ligne à 3 séries, c'est
 * 12 séries. Deux comptes différents de la même grandeur sur le même écran
 * finiraient par se contredire.
 *
 * ⚠️ **Les séries d'ÉCHAUFFEMENT ne comptent pas** (`setKind: 'warmup'`) : la
 * carte dit ce que la routine fait travailler, et un échauffement ne fait pas
 * travailler — c'est même sa définition.
 */

import { weighMuscleSets } from '../../../../shared/muscles';
import type { ExerciseSummaryDTO, RoutineDTO } from '../../../../shared/types';

export interface MuscleLoad {
  muscle: string;
  /** Séries pondérées : pleines en primaire, à moitié en secondaire. */
  sets: number;
}

export function muscleVolume(
  routine: RoutineDTO,
  exercises: readonly ExerciseSummaryDTO[],
): MuscleLoad[] {
  const parClientId = new Map(exercises.map((row) => [row.clientId, row]));
  const total = new Map<string, number>();

  for (const bloc of routine.blocks) {
    /* Tours de circuit compris — cf. l'en-tête. */
    const tours = bloc.kind === 'circuit' ? (bloc.rounds ?? 1) : 1;
    for (const ligne of bloc.lines) {
      if (ligne.setKind === 'warmup') continue;
      const exo = parClientId.get(ligne.exerciseClientId);
      if (!exo) continue;
      /*
       * 🔴 **La pondération vient de `shared/muscles.ts`** (V2 étape 9) : le
       * tableau de bord compte les mêmes séries par muscle, à partir des
       * `actual_set` cette fois. Deux demis écrits séparément auraient fini par
       * diverger, et une routine annoncée « 12 séries de pectoraux » aurait
       * produit un tableau de bord disant autre chose pour la même séance.
       */
      weighMuscleSets(exo.primaryMuscles, exo.secondaryMuscles, ligne.sets * tours, total);
    }
  }

  return [...total.entries()]
    .map(([muscle, sets]) => ({ muscle, sets }))
    /*
     * ⚠️ Trié par volume décroissant, puis par nom en **français**
     * (`localeCompare`) : la collation par défaut range toutes les majuscules
     * avant toutes les minuscules, et « Épaules » avant « abdominaux ».
     */
    .sort((a, b) => b.sets - a.sets || a.muscle.localeCompare(b.muscle, 'fr'));
}

/**
 * L'intensité d'un muscle, de 0 à 3 — ce que la carte peint.
 *
 * 🔴 **Relative au muscle le PLUS travaillé de la routine, pas absolue.** Une
 * échelle absolue dirait « peu » sur toute une séance courte et « beaucoup » sur
 * toute une séance longue : elle mesurerait la durée, pas la répartition. Or ce
 * qu'on vient lire sur une carte corporelle, c'est **où va le travail** —
 * autrement dit une comparaison interne.
 *
 * ⚠️ **Quatre niveaux, pas un dégradé continu.** Un dégradé donnerait des
 * teintes qu'on ne sait pas nommer, donc pas comparer ; quatre paliers se
 * légendent. Et la légende existe : la couleur ne porte jamais seule
 * l'information — la liste sous la carte donne les chiffres.
 */
export function muscleIntensity(loads: readonly MuscleLoad[]): Map<string, number> {
  const max = Math.max(0, ...loads.map((load) => load.sets));
  const echelle = new Map<string, number>();
  if (max === 0) return echelle;

  for (const load of loads) {
    const part = load.sets / max;
    echelle.set(load.muscle, part >= 0.75 ? 3 : part >= 0.4 ? 2 : part > 0 ? 1 : 0);
  }
  return echelle;
}
